import React from 'react';

import { cn } from '../../lib/css/className/className';
import { getColorSkin } from '../../lib/skin/getColorSkin/getColorSkin';
import { SkinColor, SkinRole } from '../../lib/skin/getColorSkin/generated/types';

import { TextInputColor } from './types';

export const cls = cn('TextInput');

type MarkedTextChunk = {
    /**
     * Названия маркеров, которые наложены на текст
     */
    marker: string[];

    /**
     * Текст, который попадает под маркер
     */
    text: string;
};

/**
 * Обновление индексов с маркированным текстом: для каждой буквы записывается ее маркер,
 * если она соответствует правилам поиска.
 *
 * @param searchRules - правило, по которому отбирается маркированный текст
 * @param textToSearchIn - текст, в котором ведется маркировка
 * @param indexesMap - объект, в который складывается результат маркировки
 * @param marker - маркер индекса (например, `error` или `highlight`)
 *
 * @returns Record<number,string[]>, где number обозначает индекс буквы, а string - ее маркер для подсветки
 * Если маркера нет, то индекс не записывается
 *
 * Пример объекта на выходе:
 *  {
 *    0: ['error'],
 *    1: ['error'],
 *    2: ['highlight'],
 *    3: ['highlight', 'error'],
 *    4: ['highlight']
 *  }
 */
function updateTextMarksIndexes(
    searchRules: RegExp[] | undefined,
    textToSearchIn: string | undefined,
    indexesMap: Record<number, string[]>,
    marker: string,
) {
    if (!searchRules || !textToSearchIn) {
        return;
    }

    searchRules.map((searchRule: RegExp) => {
        const regexRule = new RegExp(searchRule, 'g');

        let match;
        while ((match = regexRule.exec(textToSearchIn))) {
            const start = match.index;
            const end = regexRule.lastIndex;

            if (end > start) {
                for (let i = start; i < end; i++) {
                    if (!indexesMap[i]) {
                        indexesMap[i] = [];
                    }

                    indexesMap[i]?.push(marker);
                }
            }

            if (match.index === regexRule.lastIndex) {
                regexRule.lastIndex++;
            }
        }
    });
}

/**
 * Генерация частей текста с разметкой и без нее, чтобы передать подготовленные части текста с модификаторами в верстку.
 * @param content - текст, в котором нужно разметить части
 * @param indexesMap - индексы промаркированного текста
 *
 * @returns chunks - промаркированные части текста
 *
 * Пример массива нв выходе:
 * [
 *   { marker: [], text: 'одежда ' },
 *   { marker: ['highlight'], text: '-zara' },
 *   { marker: [], text: ' ' },
 *   { marker: ['highlight'], text: '-' },
 *   { marker: ['error', 'highlight'], text: '!!!' },
 *   { marker: ['highlight'], text: 'дёшево' },
 * ]
 */
function getHighlightedTextChunks(content: string, indexesMap: Record<number, string[]>) {
    const characterArray: string[] = content.split('');
    const chunks: MarkedTextChunk[] = [];
    for (let i = 0; i < characterArray.length; i++) {
        const markers = indexesMap[i];
        const lastChunk = chunks[chunks.length - 1];
        const lastChunkMarkers = lastChunk && lastChunk.marker;

        // если буква не имеет маркера:
        // - происходит ее добавление в предыдущий chunk, если он был с немаркированным текстом
        // или
        // - происходит создание нового chunk, если предыдущий был с маркированным текстом
        if (!markers) {
            if (lastChunkMarkers && lastChunkMarkers.length === 0) {
                lastChunk.text += characterArray[i] ?? '';
                continue;
            }

            chunks.push({ marker: [], text: characterArray[i] || '' });
            continue;
        }

        // если буква имеет маркер:
        // - происходит ее добавление в предыдущий chunk, если он был с маркированным текстом
        // или
        // - происходит создание нового chunk, если предыдущий был с немаркированным текстом
        if (lastChunkMarkers && lastChunkMarkers.sort().toString() === markers.sort().toString()) {
            lastChunk.text += characterArray[i] ?? '';
            continue;
        }

        chunks.push({ marker: markers, text: characterArray[i] || '' });
    }

    return chunks;
}

/**
 * Добавление подсветки для текста
 * @param highlightRules - правила подсветки текста серым цветом
 * @param errorPatterns - правила подсветки текста красным цветом
 * @param text - текст, в котором нужно добавить подсветку
 *
 * @returns textWithHighlight - ReactNode с текстом с подсветкой,
 *          hasTextErrors - пометка, есть ли в тексте части с ошибками
 */
export function getHighlightedText(
    highlightRules: RegExp[] | undefined,
    errorPatterns: RegExp[] | undefined,
    text: string,
) {
    if (text.length === 0) {
        return { textWithHighlight: null, hasTextErrors: false };
    }

    const indexesMap: Record<number, string[]> = {};
    let hasTextErrors = false;

    updateTextMarksIndexes(errorPatterns, text, indexesMap, 'error');
    updateTextMarksIndexes(highlightRules, text, indexesMap, 'highlight');

    const chunks = getHighlightedTextChunks(text, indexesMap);

    const textWithHighlight = (
        <span>
            {chunks.map((chunk, index) => {
                const markers = chunk.marker;
                if (markers.length === 0) {
                    return <span key={index} className={cls('mark')} data-content={chunk.text} />;
                }

                if (!hasTextErrors && markers.includes('error')) {
                    hasTextErrors = true;
                }

                const classModifications: Record<string, boolean> = {};
                for (const marker of markers) {
                    classModifications[marker] = true;
                }

                return <span key={index} className={cls('mark', classModifications)} data-content={chunk.text} />;
            })}
        </span>
    );

    return { textWithHighlight, hasTextErrors };
}

export function getColorSkinClass(
    color: TextInputColor,
    { isDisabled, isHovered }: { isDisabled: boolean; isHovered: boolean },
) {
    const colorSkinMap: Record<TextInputColor, SkinColor | SkinRole> = {
        contour: 'gray-transparent-contour',
        contrast: 'gray-neutral',
        normal: 'gray-low-contrast',
        transparent: 'gray-transparent',
    };

    return getColorSkin(colorSkinMap[color], {
        isDisabled,
        isHovered,
    });
}
